/**
 * NuGuard AI - Error Boundary Component
 *
 * Catches unhandled React render errors and shows a user-friendly fallback
 * instead of crashing to a blank white screen.
 */

import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { reloadOnceForStaleChunk } from '../utils/staleChunkReload';

interface Props {
  children: ReactNode;
  /** Optional fallback UI override */
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  /** Returns true for "Failed to fetch dynamically imported module" errors — stale chunk after a new deploy. */
  static isChunkLoadError(error: Error | null): boolean {
    if (!error) return false;
    return (
      error.message.includes('Failed to fetch dynamically imported module') ||
      error.message.includes('Importing a module script failed') ||
      error.name === 'ChunkLoadError'
    );
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.setState({ errorInfo });
    console.error('[ErrorBoundary] Uncaught error:', error, errorInfo.componentStack);
    // Stale asset chunk after a new deployment → reload once to get the fresh index.html.
    if (ErrorBoundary.isChunkLoadError(error)) {
      reloadOnceForStaleChunk();
    }
  }

  handleReload = (): void => {
    window.location.reload();
  };

  handleGoHome = (): void => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    window.location.hash = '#/';
  };

  handleReset = (): void => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-[400px] flex items-center justify-center p-8">
          <div className="max-w-md w-full text-center">
            <div className="mx-auto w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center mb-6">
              <AlertTriangle className="w-8 h-8 text-red-600 dark:text-red-400" />
            </div>
            <h2 className="text-xl font-semibold text-stone-900 dark:text-white mb-2">
              Something went wrong
            </h2>
            <p className="text-stone-500 dark:text-slate-400 mb-2">
              An unexpected error occurred. This page failed to render properly.
            </p>
            {this.state.error && (
              <p className="text-xs text-stone-400 dark:text-slate-500 font-mono mb-6 break-all">
                {this.state.error.message}
              </p>
            )}
            <div className="flex gap-3 justify-center">
              <button
                onClick={this.handleReset}
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-medium transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Try Again
              </button>
              <button
                onClick={this.handleReload}
                className="inline-flex items-center gap-2 px-4 py-2 bg-stone-200 dark:bg-nuguard-card hover:bg-stone-300 dark:hover:bg-white/[0.06] text-stone-900 dark:text-white rounded-lg text-sm font-medium transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Reload Page
              </button>
              <button
                onClick={this.handleGoHome}
                className="inline-flex items-center gap-2 px-4 py-2 bg-stone-200 dark:bg-nuguard-card hover:bg-stone-300 dark:hover:bg-white/[0.06] text-stone-900 dark:text-white rounded-lg text-sm font-medium transition-colors"
              >
                <Home className="w-4 h-4" />
                Go to Dashboard
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
